import React from 'react';
import { Navigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ShieldX } from 'lucide-react';

export default function ProtectedRoute({ children }) {
  const { user, loading, logout } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFBFC] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-slate-200 border-t-[#002FA7] rounded-full animate-spin" />
          <span className="text-slate-400 text-sm tracking-widest uppercase">Loading</span>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // Allow admins through always
  if (user.role === 'admin') {
    return children;
  }

  // Block inactive agents (no approved application)
  if (user.agent_status !== 'active') {
    return (
      <div className="min-h-screen bg-[#FAFBFC] flex items-center justify-center px-6" data-testid="access-denied">
        <div className="max-w-md text-center">
          <div className="w-16 h-16 bg-red-50 border border-red-200 rounded-full flex items-center justify-center mx-auto mb-6">
            <ShieldX size={28} className="text-red-400" />
          </div>
          <h1 className="text-2xl font-['Cabinet_Grotesk'] font-bold text-slate-900 mb-3">Access Restricted</h1>
          <p className="text-sm text-slate-500 leading-relaxed mb-6">
            The Agent Portal is available only to active agents with an approved application. If you've recently applied, please wait for your application to be reviewed and approved.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/apply" className="text-sm bg-[#002FA7] text-white px-6 py-2.5 rounded-full font-medium hover:bg-[#0040D6] transition-colors" data-testid="apply-link">
              Apply Now
            </Link>
            <button onClick={logout} className="text-sm border border-slate-200 text-slate-600 px-6 py-2.5 rounded-full font-medium hover:bg-slate-50 transition-colors" data-testid="logout-btn">
              Sign Out
            </button>
          </div>
          <p className="text-xs text-slate-400 mt-6">Already applied? Check your email for status updates.</p>
        </div>
      </div>
    );
  }

  return children;
}
